/** THE EDITOR'S VIEW LAYERS (sheet rows 94–98): six layers the architect can
 *  switch off or fade from the layers panel, so the walls read through a
 *  furnished plan.
 *
 *  View state only: never in the document, never in the exports. Only a layer
 *  on at 100% can be edited; a faded one is there to be read past, so clicks
 *  and the stretch window go through it. A layer below 100% stays in the DOM,
 *  wrapped and marked (`LayerWrap`), because the assistant's plan capture
 *  draws every layer whatever the panel says (`asistent/captura.ts`).
 */
import { createElement, type ReactNode } from "react";
import { felulPiesei, SANITARE } from "../../plan/arhMobilierPlan";
import { aparatul } from "../design/electric";

export const LAYER_IDS = ["walls", "furniture", "sanitary", "sockets", "lights", "floors"] as const;
export type LayerId = typeof LAYER_IDS[number];
export type Layer = { on: boolean; opacity: number };
export type Layere = Record<LayerId, Layer>;

export const LAYERE_IMPLICITE: Layere = Object.fromEntries(
  LAYER_IDS.map((id) => [id, { on: true, opacity: 1 }])) as Layere;

/** The attribute that marks a layer's wrapper; the capture looks for it. */
export const LAYER_ATTR = "data-layer";

/** The walls can be faded but never hidden: a plan without walls is no plan
 *  (sheet rows 94–95, «pereții ar trebui să rămână vizibili»). The other
 *  layers stop at 10%, so "invisible" is always the off button. */
export const minimulLayerului = (id: LayerId): number => (id === "walls" ? 0.2 : 0.1);

export function clampLayer(id: LayerId, v: Layer): Layer {
  const o = Math.round(Math.min(1, Math.max(minimulLayerului(id), v.opacity)) * 10) / 10;
  return { on: id === "walls" ? true : v.on, opacity: o };
}

export const visibleOpacity = (l: Layer): number => (l.on ? l.opacity : 0);
export const editable = (l: Layer): boolean => l.on && l.opacity === 1;

/** Kitchen sinks and the plan-symbol WC sit with the bathroom fixtures in the
 *  editor: every object with a water point. The DWG sorts its own layers with
 *  `export/desen::stratulPiesei`, from `SANITARE` alone. */
export const SANITARE_EXTRA: ReadonlySet<string> =
  new Set(["wc_clasic", "chiuveta", "insula_chiuveta"]);

/** A piece's layer; null for structure (stairs, lift, shaft), which no layer
 *  hides. A kind of no list, the CAD library's included, is furniture. */
export function layerOfPiece(kind: string): LayerId | null {
  if (felulPiesei(kind)?.structura) return null;
  return SANITARE.has(kind) || SANITARE_EXTRA.has(kind) ? "sanitary" : "furniture";
}

/** A device's layer, by catalogue group: switches and lights together, every
 *  other device (weak currents and the board included) with the sockets. */
export function layerOfDevice(kind: string): LayerId {
  const grupa = aparatul(kind)?.grupa;
  return grupa === "Comenzi" || grupa === "Iluminat" ? "lights" : "sockets";
}

/** The layers an electrical item is drawn on: a box's devices' layers, sockets
 *  first; an empty box goes with the sockets. */
export function layersOfItem(a: { kind: string; devices?: readonly string[] }): LayerId[] {
  if (a.kind !== "doza") return [layerOfDevice(a.kind)];
  const ale = new Set((a.devices ?? []).map(layerOfDevice));
  const out = (["sockets", "lights"] as const).filter((l) => ale.has(l));
  return out.length ? out : ["sockets"];
}

/** The tools that draw walls or openings: picking one brings faded walls back.
 *  The stretch window (`intinde`) is not one of them: it moves what it takes. */
const UNELTE_DE_ZID: ReadonlySet<string> = new Set([
  "perete", "arc", "parapet", "balcon", "usa", "usaDubla", "usaGlisanta", "fereastra",
  "demoleaza", "camera", "cameraL", "cameraT",
]);

/** The layer a tool puts its object on, or null for a tool of no layer. */
export function layerOfTool(unealta: string, piesa: string, aparat: string): LayerId | null {
  if (unealta === "mobila") return layerOfPiece(piesa);
  if (unealta === "aparat") return layerOfDevice(aparat);
  return UNELTE_DE_ZID.has(unealta) ? "walls" : null;
}

export const layerOfRoute = (circuit: string): LayerId =>
  (circuit === "lumina" ? "lights" : "sockets");

/** The highest visible opacity among some layers: what an element shared by
 *  them (a mixed box's frame) is drawn at. */
export const opacitateaLor = (layere: Layere, ids: readonly LayerId[]): number =>
  Math.max(0, ...ids.map((id) => visibleOpacity(layere[id])));

export const toateEditabile = (layere: Layere, ids: readonly LayerId[]): boolean =>
  ids.every((id) => editable(layere[id]));

/** Layers are the viewer's, not the module's: they stay in the browser, one
 *  setting for every module, as the structure editor keeps its own
 *  (`structure/EditorStructura::CHEIE_STRATURI`). */
export const CHEIE_LAYERE = "ob.editor-tronson.layere";

/** The stored layers, each one checked on its own: a missing, unknown or
 *  malformed entry gives that layer its default, never a broken editor. */
export function loadLayers(): Layere {
  let brut: unknown = null;
  try {
    brut = JSON.parse(localStorage.getItem(CHEIE_LAYERE) ?? "null");
  } catch {
    brut = null;
  }
  const tinut = (brut && typeof brut === "object" ? brut : {}) as Record<string, unknown>;
  return Object.fromEntries(LAYER_IDS.map((id) => {
    const v = tinut[id] as { on?: unknown; opacity?: unknown } | undefined;
    const bun = !!v && typeof v.on === "boolean" && typeof v.opacity === "number"
      && Number.isFinite(v.opacity) && v.opacity >= 0 && v.opacity <= 1;
    return [id, bun ? clampLayer(id, { on: v!.on as boolean, opacity: v!.opacity as number })
      : LAYERE_IMPLICITE[id]];
  })) as Layere;
}

export function saveLayers(l: Layere): void {
  try {
    localStorage.setItem(CHEIE_LAYERE, JSON.stringify(l));
  } catch {
    // No storage (a private window, a full quota): the layers stay on screen.
  }
}

/** A LAYER'S WRAPPER. At visible opacity 1 there is none, so a plan with every
 *  layer at 100% draws exactly the markup it drew before layers existed. Below
 *  1 the wrapper carries only the layer's opacity, or `display="none"` when it
 *  is off; what it wraps keeps its own opacity inside it. */
export function LayerWrap({ layere, ids, children }: {
  layere?: Layere; ids: readonly LayerId[]; children: ReactNode;
}): ReactNode {
  if (!layere || !ids.length) return children;
  const o = opacitateaLor(layere, ids);
  if (o >= 1) return children;
  return createElement("g", {
    [LAYER_ATTR]: ids.join(" "),
    ...(o > 0 ? { opacity: o } : { display: "none" }),
  }, children);
}
